// bluaIQ — Payment state machine + settlement rules. PURE, no I/O.
//
// Closes the PAYMENT half of the "three remaining Day-1 money write paths"
// (D-25AGO-09), against docs/SPEC_STATE_TRANSITIONS_MONEY_CLUSTER_V1.md §3.
//
// THE GAP THIS CLOSES. No route in the server transitioned an existing payment
// to PAID. `payment.update` did not exist; the ONLY payment status mutation was
// the CF-3 soft cancel (SCHEDULED → CANCELLED, flag-gated, allowlisted).
// `paidDate` was settable at creation and never after. So the Payments module
// could show "verify before marking paid" and offer no way to mark it paid.
//
// The permitted graph (spec §3.2):
//
//        SCHEDULED ──cancel──▶ CANCELLED (terminal)   [CF-3, flag-gated]
//            │
//            └──promote──▶ PENDING ──settle──▶ PAID (terminal)
//                             │                 ▲
//                          (derived)             │
//                          OVERDUE ──settle──────┘
//
// OVERDUE IS A DERIVED PRESENTATION STATE (spec R-3.2.a): `PENDING` past its
// dueDate. It MUST NOT be persisted. It appears in this map ONLY because the
// create-time zod enum still accepts it (spec F-5, a separate finding this task
// does not reopen), so rows carrying it can exist and MUST remain settleable
// rather than becoming permanently stuck. Nothing here WRITES it.
//
// PAID AND CANCELLED ARE TERMINAL AND IRREVERSIBLE. PAID is the assertion that
// money moved; CANCELLED is a soft cancel whose row and amount survive for
// audit. A mistake is corrected by a compensating record that leaves both rows
// visible — never by an "undo paid" affordance, which is PROHIBITED in any form.

export const PAYMENT_STATUSES = ['PENDING', 'PAID', 'OVERDUE', 'SCHEDULED', 'CANCELLED'] as const
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number]

/** Permitted edges. Everything not listed is PROHIBITED (spec §3.2). */
const PERMITTED: Record<PaymentStatus, readonly PaymentStatus[]> = {
  PENDING: ['PAID'],
  OVERDUE: ['PAID'],
  // Owned by the flag-gated CF-2/CF-3 routes — listed so the matrix is complete,
  // NOT re-implemented here. This module never widens their two-key allowlist.
  SCHEDULED: ['CANCELLED', 'PENDING'],
  PAID: [], // terminal — money moved
  CANCELLED: [], // terminal — soft cancel, row retained
}

/**
 * The ONLY states a settlement may start from. SCHEDULED is deliberately absent:
 * spec §3.2 lists `SCHEDULED → PAID` as PROHIBITED because paying a row that was
 * never due skips every readiness guard — it MUST pass through PENDING first.
 */
export const SETTLEABLE_STATUSES = ['PENDING', 'OVERDUE'] as const

/**
 * A payment minted by the CF-2 schedule-payments flow. Spec §3.2 PROHIBITED:
 * "any status transition on a payment whose sourceType is DRAFT_SCHEDULE_LINE
 * outside the CF-2/CF-3 routes". The generic settle route is outside them, so a
 * draft-line-sourced row is never settleable here. This also answers spec §10
 * question 3 in its conservative direction, which is the direction the spec's
 * own PROHIBITED list already took.
 */
export const NON_SETTLEABLE_SOURCE_TYPE = 'DRAFT_SCHEDULE_LINE'

export function isPaymentStatus(value: unknown): value is PaymentStatus {
  return typeof value === 'string' && (PAYMENT_STATUSES as readonly string[]).includes(value)
}

/**
 * True iff `from → to` is a permitted edge. A self-transition is NEVER an edge
 * (it would mask a double-submit) — callers treat "status unchanged" as a no-op.
 */
export function isValidPaymentTransition(from: PaymentStatus, to: PaymentStatus): boolean {
  if (from === to) return false
  return PERMITTED[from]?.includes(to) ?? false
}

export function isTerminalPaymentStatus(status: PaymentStatus): boolean {
  return PERMITTED[status].length === 0
}

/** True iff a row in this status may be settled to PAID by the generic settle
 *  route. Deliberately a whitelist, not `!isTerminal(...)`: adding a state
 *  without deciding its settleability must NOT silently make it settleable. */
export function isSettleableStatus(status: unknown): status is 'PENDING' | 'OVERDUE' {
  return typeof status === 'string' && (SETTLEABLE_STATUSES as readonly string[]).includes(status)
}

/** True iff the row's provenance permits settlement here. A NULL/absent
 *  sourceType is the ordinary case (every non-CF-2 payment) and is permitted. */
export function isSettleableSourceType(sourceType: unknown): boolean {
  return sourceType !== NON_SETTLEABLE_SOURCE_TYPE
}

/** Stable machine codes clients pin — never the English strings. */
export const SETTLEMENT_CODES = {
  PAYMENT_NOT_FOUND: 'PAYMENT_NOT_FOUND',
  PAYMENT_NOT_SETTLEABLE: 'PAYMENT_NOT_SETTLEABLE',
  INVALID_PAYMENT_SOURCE: 'INVALID_PAYMENT_SOURCE',
  PAYMENT_ALREADY_SETTLED: 'PAYMENT_ALREADY_SETTLED',
  MISSING_IDEMPOTENCY_KEY: 'MISSING_IDEMPOTENCY_KEY',
} as const

/**
 * What settlement does NOT do. Stated explicitly because the Payments module
 * already tells the user that payments are not connected to Budget, Calendar/
 * Gantt or QuickBooks, and that honesty must survive the arrival of a write
 * verb: marking a payment paid is a RECORD of a settlement, not a transfer.
 */
export const SETTLEMENT_DOWNSTREAM = {
  movesMoney: false,
  updatesBudget: false,
  updatesCalendarOrGantt: false,
  updatesQuickBooks: false,
} as const

/**
 * Decide a replayed settlement. The route's conditional UPDATE matches zero rows
 * both when a concurrent request already settled the payment AND when the SAME
 * request is being retried — those two are not the same outcome and must not
 * share a response. A row already PAID under the SAME idempotency key is the
 * caller's own write arriving twice: return the same row, 200, and write no
 * second audit event. Anything else is a genuine conflict.
 */
export function classifySettlementReplay(
  row: { status: unknown; settlementIdempotencyKey: unknown } | null,
  idempotencyKey: string,
): 'idempotent_replay' | 'conflict' | 'not_found' {
  if (!row) return 'not_found'
  if (row.status === 'PAID' && row.settlementIdempotencyKey === idempotencyKey) return 'idempotent_replay'
  return 'conflict'
}

/**
 * The SAFE response shape for a settled payment. Mirrors
 * `toSafeCancelledPaymentResponse` (CF-3): an explicit allowlist of fields, so a
 * future column cannot reach the client by being spread in.
 *
 * `paidBy` is DELIBERATELY ABSENT. It holds a raw Clerk user id, and the house
 * rule is that raw identity never reaches the client (the same leak class the
 * uploader-label helper exists to close). The actor is recorded on the row and
 * in the operational event, where it belongs; the settling user already knows
 * who they are. `sourceSnapshot` is likewise never returned.
 */
export function toSafeSettledPaymentResponse(
  payment: {
    id: string
    status: unknown
    amount: unknown
    currency: unknown
    paidDate: Date | string | null | undefined
    dueDate: Date | string | null | undefined
  },
  helpers: {
    amountToNumber: (value: unknown) => number | null
    amountToMoney: (value: unknown) => { cents: number; amount: string; currency: 'USD' } | null
  },
): {
  id: string
  status: string
  amount: number | null
  amountMoney: { cents: number; amount: string; currency: 'USD' } | null
  currency: string | null
  paidDate: string | null
  dueDate: string | null
} {
  const iso = (value: Date | string | null | undefined): string | null => {
    if (!value) return null
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d.toISOString()
  }
  return {
    id: payment.id,
    status: typeof payment.status === 'string' ? payment.status : 'PAID',
    amount: helpers.amountToNumber(payment.amount),
    amountMoney: helpers.amountToMoney(payment.amount),
    currency: typeof payment.currency === 'string' ? payment.currency : null,
    paidDate: iso(payment.paidDate),
    dueDate: iso(payment.dueDate),
  }
}
